import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { fetchSuggestions } from '../../lib/api';
import { cn } from '../../lib/utils';

interface SuggestionChipsProps {
  onSelect: (suggestion: string) => void;
  visible?: boolean;
}

export function SuggestionChips({ onSelect, visible = true }: SuggestionChipsProps) {
  const [suggestions, setSuggestions] = useState<string[]>([]);

  useEffect(() => {
    if (visible) {
      fetchSuggestions()
        .then((data) => setSuggestions(data.suggestions.slice(0, 4)))
        .catch(() => setSuggestions([]));
    }
  }, [visible]);

  if (!visible || suggestions.length === 0) {
    return null;
  }

  const gradients = [
    'from-primary/5 via-purple-500/5 to-pink-500/5',
    'from-purple-500/5 via-pink-500/5 to-primary/5',
    'from-pink-500/5 via-primary/5 to-purple-500/5',
  ];

  const hoverBorders = [
    'hover:border-primary/40 hover:shadow-lg hover:shadow-primary/10',
    'hover:border-purple-500/40 hover:shadow-lg hover:shadow-purple-500/10',
    'hover:border-pink-500/40 hover:shadow-lg hover:shadow-pink-500/10',
  ];

  const hoverGradients = [
    'from-primary/10 to-transparent',
    'from-purple-500/10 to-transparent',
    'from-pink-500/10 to-transparent',
  ];

  return (
    <div className="border-t border-border bg-muted/30 px-4 py-3 sm:px-6 sm:py-4">
      <div className="mx-auto max-w-3xl">
        <div className="flex items-center gap-2 mb-3 sm:mb-4">
          <Sparkles className="h-4 w-4 text-gray-500" />
          <span className="text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">
            Try asking
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:gap-3">
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              onClick={() => onSelect(suggestion)}
              className={cn(
                'group relative overflow-hidden rounded-xl border border-border/50 px-3 py-2 sm:px-4 sm:py-2.5',
                'text-xs sm:text-sm text-foreground font-medium text-left font-family-messina-sans',
                'transition-all duration-200 cursor-pointer',
                'focus:outline-none focus:ring-2 focus:ring-ring',
                `bg-gradient-to-br ${gradients[index % gradients.length]}`,
                hoverBorders[index % hoverBorders.length]
              )}
            >
              <div
                className={cn(
                  'absolute inset-0 bg-gradient-to-r opacity-0 group-hover:opacity-100 transition-opacity',
                  hoverGradients[index % hoverGradients.length]
                )}
              />
              <span className="relative">{suggestion}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
